<template>
  <v-card flat>
    <v-card-text>
      <!-- ================================================================= -->
      <!-- HEADER & TABLE                                                  -->
      <!-- ================================================================= -->
      <div class="d-flex justify-space-between align-center mb-4">
        <h3 class="headline">Benutzer verwalten</h3>
        <v-btn color="default" dark @click="openUserDialog(null)">
          <v-icon left>mdi-plus</v-icon>
          Benutzer erstellen
        </v-btn>
      </div>

      <v-data-table
          :headers="headers"
          :items="userList"
          :loading="loading"
          class="elevation-1"
      >
        <template v-slot:item.actions="{ item }">
          <v-icon small class="mr-2" title="Benutzer bearbeiten" @click="openUserDialog(item)">mdi-pencil</v-icon>
          <v-icon small color="red" title="Benutzer löschen" @click="confirmDeleteUser(item)">mdi-delete</v-icon>
        </template>
      </v-data-table>
    </v-card-text>

    <!-- ================================================================= -->
    <!-- DIALOGS                                                         -->
    <!-- ================================================================= -->
    <!-- Dialog 1: Create/Edit User -->
    <v-dialog v-model="dialog.edit" persistent max-width="600px">
      <v-card>
        <v-card-title>
          <span class="headline">{{ isEditing ? 'Benutzer bearbeiten' : 'Neuer Benutzer' }}</span>
        </v-card-title>
        <v-card-text>
          <v-text-field v-model="editableUser.username" label="Benutzername" required outlined class="mb-4"></v-text-field>
          <v-select v-model="editableUser.group" :items="userGroups" label="Rolle / Gruppe" required outlined class="mb-4"></v-select>
          <v-text-field v-model="editableUser.password" type="password" label="Passwort" outlined
                        :placeholder="isEditing ? 'Leer lassen, um nicht zu ändern' : ''"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeUserDialog">Abbrechen</v-btn>
          <v-btn color="primary" text :loading="isSaving" @click="saveUser">Speichern</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog 2: Delete Confirmation -->
    <v-dialog v-model="dialog.delete" max-width="450px">
      <v-card v-if="userToDelete">
        <v-card-title class="headline">Benutzer "{{ userToDelete.username }}" löschen?</v-card-title>
        <v-card-text>
          Sind Sie sicher, dass Sie diesen Benutzer permanent löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialog.delete = false">Abbrechen</v-btn>
          <v-btn color="red darken-1" text :loading="isSaving" @click="deleteUser">Löschen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snack.state" :timeout="snack.timeout" :color="snack.color" top right>{{ snack.message }}</v-snackbar>
  </v-card>
</template>

<script>
import axios from 'axios';

const defaultUser = { username: '', group: 'benutzer', password: '' };

export default {
  name: "UserManagement",
  data() {
    return {
      userList: [],
      loading: false,
      isSaving: false,
      dialog: { edit: false, delete: false },
      isEditing: false,
      editableUser: { ...defaultUser },
      userToDelete: null,
      userGroups: ['benutzer', 'pruefer', 'admin'],
      headers: [
        { text: 'Benutzername', align: 'start', value: 'username' },
        { text: 'Rolle', value: 'group' },
        { text: 'Aktionen', value: 'actions', sortable: false, align: 'end' },
      ],
      snack: { state: false, color: 'success', timeout: 3000, message: '' },
    }
  },
  mounted() {
    this.fetchUsers();
  },
  methods: {
    fetchUsers() {
      this.loading = true;
      axios.get('/auth/userlist')
          .then(response => { this.userList = response.data; })
          .catch(() => this.showSnackbar("Benutzerliste konnte nicht geladen werden.", "error"))
          .finally(() => this.loading = false);
    },

    openUserDialog(user) {
      if (user) { // Editing existing user
        this.isEditing = true;
        this.editableUser = { ...user, password: '' }; // Copy user, reset password field
      } else { // Creating new user
        this.isEditing = false;
        this.editableUser = { ...defaultUser };
      }
      this.dialog.edit = true;
    },

    closeUserDialog() {
      this.dialog.edit = false;
      this.editableUser = { ...defaultUser };
    },

    saveUser() {
      this.isSaving = true;
      let promise;

      // Don't send an empty password string on update
      const payload = { ...this.editableUser };
      if (this.isEditing && !payload.password) {
        delete payload.password;
      }

      if (this.isEditing) {
        promise = axios.patch(`/auth/user/${payload._id}`, payload);
      } else {
        promise = axios.post('/auth/register', payload);
      }

      promise.then(() => {
        this.showSnackbar(`Benutzer "${payload.username}" erfolgreich gespeichert.`);
        this.closeUserDialog();
        this.fetchUsers();
      })
          .catch(() => this.showSnackbar("Fehler beim Speichern des Benutzers.", "error"))
          .finally(() => this.isSaving = false);
    },

    confirmDeleteUser(user) {
      this.userToDelete = user;
      this.dialog.delete = true;
    },

    deleteUser() {
      this.isSaving = true;
      axios.delete(`/auth/user/${this.userToDelete._id}`)
          .then(() => {
            this.showSnackbar(`Benutzer "${this.userToDelete.username}" wurde gelöscht.`);
            this.dialog.delete = false;
            this.fetchUsers();
          })
          .catch(() => this.showSnackbar("Fehler beim Löschen des Benutzers.", "error"))
          .finally(() => this.isSaving = false);
    },

    showSnackbar(message, color = 'success') {
      this.snack.message = message;
      this.snack.color = color;
      this.snack.state = true;
    }
  }
}
</script>